import kemuCore from '@kemu-io/kemu-core';
import { HubServiceState, WidgetState, WidgetGroupState, WidgetType, RecipeWidget } from '@kemu-io/kemu-core/types';
import { RemoveInvokeError } from '@kemu-io/hs/ri';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { defineGroupFieldVariables } from '../widgetGroupHelpers';
import connectionManager from '@src/app/kemuHub/connectionManager';
import { removeWidget } from '@src/app/recipe/utils';
import { showGlobalNotification } from '@src/features/interface/interfaceSlice';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';

/**
 * Iterates over a list of added widgets, performing necessary initialization tasks.
 * This includes subscribing to HubService events, calling kemuCore.initializeWidget for HubService and Script widgets,
 * and defining group field variables for WidgetGroup widgets. It also handles potential errors during initialization.
 * @returns {Promise<void>} A promise that resolves when all initializations are complete.
 */
export const initializeAddedWidgets = async (config: {
  /** The ID of the recipe */
  recipeId: string,
  /** The ID of the thing */
  thingId: string,
  /** An array of widgets that have been added */
  addedWidgetsList: RecipeWidget[],
  /** 
   * A map of the added widgets, used for potential removal on error.
   * This object will be mutated if an error occurs during initialization.
   */
  addedWidgetsMap?: { [key: string]: RecipeWidget },
  /** The thunk API object from Redux Toolkit, used for dispatching actions and getting state */
  thunkApi: GetThunkAPI<unknown>,
  /** The ID of the current user, used for checking collectionInfo ownership */
  userId?: string,
}): Promise<void> => {
  const { recipeId, thingId, addedWidgetsList, addedWidgetsMap, thunkApi, userId } = config;
  const t = getGlobalIntl();

  for (const widget of addedWidgetsList) {
    // Subscribe to services
    if (widget.type === WidgetType.hubService) {
      const hubServiceState = widget.state as HubServiceState;
      if (hubServiceState.service?.eventEmitter) {
        const hc = connectionManager.getConnector();
        hc.subscribeToServiceEvents({
          targetService: {
            serviceName: hubServiceState.service.name,
            version: hubServiceState.service.version
          },
          listener: {
            recipeId: recipeId,
          }
        }).catch((e) => {
          console.error('Error subscribing to service events for new widget', e);
        });
      }

      try {
        await kemuCore.initializeWidget({
          recipePoolId: recipeId,
          thingRecipeId: thingId,
          widgetRecipeId: widget.id,
        });
      } catch (e) {
        if (e?.errCode !== RemoveInvokeError.FunctionNotFound) {
          const functionNotDefined = !!(typeof e === 'string' && e.match('^Function ".*" not found.$'));
          if (!functionNotDefined) {
            if (addedWidgetsMap) {
              delete addedWidgetsMap[widget.id];
            }

            await removeWidget(recipeId, thingId, widget.id);

            thunkApi.dispatch(showGlobalNotification({
              title: t('Errors.ServiceInitError'),
              message: typeof e === 'string' ? e : e.error || e.message,
              type: 'error',
            }));
          }
        }
      }
    }

    if (widget.type === WidgetType.widgetGroup) {
      const groupState = widget.state as WidgetState<WidgetGroupState>;
      // Remove collection info if pasted from another user. This happens if users
      // share widgets by just sharing the encoded text string.
      if (userId && groupState.collectionInfo?.userId && (groupState.collectionInfo?.userId !== userId)) {
        groupState.collectionInfo = undefined;
      }
      // re-define field variables subscriptions
      defineGroupFieldVariables(widget);
    }

    // Initialize widget that require initialization.
    // This includes script and variable widgets.
    // const requireInitialization = [
    //   WidgetType.script,
    //   WidgetType.variable,
    //   WidgetType.object,
    //   WidgetType.objectCombine,
    // ];

    // UPDATE: 12/06/2025, all widgets are initialized by default.
    // if (requireInitialization.includes(widget.type)) {
      try {
        await kemuCore.initializeWidget({
          recipePoolId: recipeId,
          thingRecipeId: thingId,
          widgetRecipeId: widget.id,
        });
      } catch (e) {
        console.error(`Error initializing widget ${widget.id} (${widget.type}): `, e);
      }
    // }
  }
};
